/* eslint-disable @typescript-eslint/strict-boolean-expressions -- for now */
// src/store/slices/connectionSlice.ts
// Updated to work with P2PStackManager

import { type Unsubscribe, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { createLogger, type Logger } from '@/utils/logger';
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import type { MessageHandlerResult, MessageStats } from "@/services/abstract/poc/messaging.service.interface";
import type { P2PStackOptions } from "@/services/abstract/poc/p2p.stack-manager.interface";
import { disconnectPeer } from "@/store/thunks/connection/disconnectPeer";
import { reconnectPeer } from "@/store/thunks/connection/reconnectPeer";
import { PEER_DISCOVERY_TOPIC } from "@/config/networkConstants";

import { handleIncomingMessage } from "./messagingSlice";

/* -------------------------------------------------------------------------- */
/*  Module-level Logger (for extraReducers)                                   */
/* -------------------------------------------------------------------------- */
const log: Logger = createLogger({ namespace: 'six3ro:p2p:connection' });

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SingleConnectionState {
  sixId: string;
  status: ConnectionStatus;
  peerId: string | null;
  connectedPeers: number;
  errorMessage: string | null;
  isP2PStackInitialized: boolean;
  activeTopics: Record<string, () => void>;
  initializationProgress: {
    libp2p: boolean;
    helia: boolean;
    messaging: boolean;
  };
  connectionDetails: Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }>;
  messageDetails: Array<{
    peerId: string;
    messageCount: number;
    lastMessage: string;
    lastMessageTime: number;
    isConnected: boolean;
  }>;
  topicDetails: Array<{
    topic: string;
    messageCount: number;
    lastMessage: string;
    lastSenderPeerId: string;
    lastMessageTime: number;
    subscriberCount: number;
  }>;
}

export interface ConnectionState {
  connections: SingleConnectionState[];
}

/* -------------------------------------------------------------------------- */
/*  Pure Helper Functions                                                     */
/* -------------------------------------------------------------------------- */

export const createInitialConnection = (sixId: string): SingleConnectionState => ({
  sixId,
  status: 'disconnected',
  peerId: null,
  connectedPeers: 0,
  errorMessage: null,
  isP2PStackInitialized: false,
  activeTopics: {},
  initializationProgress: { libp2p: false, helia: false, messaging: false },
  connectionDetails: [],
  messageDetails: [],
  topicDetails: [],
});

export const findConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState | undefined =>
  connections.find(connection => connection.sixId === sixId);

const ensureConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState[] =>
  findConnection(connections, sixId)
    ? connections
    : [...connections, createInitialConnection(sixId)];

const updateConnection = (
  connections: SingleConnectionState[],
  sixId: string,
  updater: (connection: SingleConnectionState) => SingleConnectionState
): SingleConnectionState[] =>
  connections.map(connection =>
    connection.sixId === sixId ? updater(connection) : connection
  );

const removeConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState[] =>
  connections.filter(connection => connection.sixId !== sixId);

/* -------------------------------------------------------------------------- */
/*  Initial State                                                             */
/* -------------------------------------------------------------------------- */

const initialState: ConnectionState = {
  connections: [],
};

/* -------------------------------------------------------------------------- */
/*  Thunks                                                                    */
/* -------------------------------------------------------------------------- */

export const initializeP2PStack = createAppAsyncThunk(
  'connection/initializeP2PStack',
  async(
    options: P2PStackOptions,
    { extra: { pocServices, loggerService }, dispatch, rejectWithValue },
  ) => {
    const thunkLog = loggerService.createLogger({ namespace: 'six3ro:p2p:connection' });
    const { p2pStackManagerService: p2pStackService } = pocServices;
    const { sixId } = options;

    try {
      // Create Redux callback bridge for this specific six
      const messageCallback = (result: MessageHandlerResult, stats: MessageStats) => {
        thunkLog.debug(`Dispatching incoming message for sixId: ${sixId} with result:`, result);
        thunkLog.trace(`Message stats - peer: ${stats.fromPeer}, topic: ${stats.topic}, type: ${stats.messageType}`);

        // Dispatch message handling
        void dispatch(handleIncomingMessage({ ...result, sixId }));

        // Use raw message content from stats (JSON stringified message)
        const messageContent = stats.messageContent ?? 'No content';

        // Record stats for observability
        dispatch(recordMessageStats({
          sixId,
          fromPeer: stats.fromPeer,
          topic: stats.topic,
          timestamp: stats.timestamp,
          messageType: stats.messageType,
          messageContent
        }));
      };

      // Create status update callback to update subscriber counts
      const statusUpdateCallback = (sixId: string, subscriberCounts: Record<string, number>) => {
        dispatch(updateTopicSubscriberCounts({ sixId, subscriberCounts }));
      };

      // Create connection update callback to update connection details in Redux
      const connectionUpdateCallback = (sixId: string, connections: Array<{
        peerId: string;
        address: string;
        isDirect: boolean;
        isRelay: boolean;
        direction: 'inbound' | 'outbound';
      }>) => {
        dispatch(updateConnectionDetails({ sixId, connections }));
      };

      // Initialize stack with manager
       await p2pStackService.initializeStack({
        ...options,
        onMessage: messageCallback,
        onStatusUpdate: statusUpdateCallback,
        onConnectionUpdate: connectionUpdateCallback,
      });

      thunkLog.info(`P2P stack initialized for six: ${sixId}`);

      // Record subscribed topics immediately so they show in UI even without messages
      const topics = [
        PEER_DISCOVERY_TOPIC(sixId),
        options.fileTransferTopic,
        options.fileListTopic,
      ];

      topics.forEach(topic => {
        dispatch(recordSubscribedTopic({ sixId, topic }));
      });

      // Get the node to retrieve peer information
      const libp2pNode = p2pStackService.getLibp2pNode(sixId);
      if (libp2pNode === null) {
        throw new Error('LibP2P node not ready after initialization');
      }

      return {
        sixId,
        peerId: libp2pNode.peerId.toString(),
        peers: libp2pNode.getPeers().length,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ sixId, message: errorMessage });
    }
  },
);

export const disconnectP2PStack = createAppAsyncThunk(
  'connection/disconnectP2PStack',
  async(
    { sixId }: { sixId: string },
    { extra: { pocServices }, rejectWithValue },
  ) => {
    const { p2pStackManagerService: p2pStackService } = pocServices;
    try {
      await p2pStackService.shutdownStack(sixId);
      return { sixId };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ sixId, message: errorMessage });
    }
  },
);

export const publishMessage = createAppAsyncThunk(
  'connection/publishMessage',
  async(
    { sixId, topic, data }: { sixId: string; topic: string; data: string | Uint8Array },
    { extra: { pocServices }, rejectWithValue },
  ) => {
    const { p2pStackManagerService: p2pStackService } = pocServices;
    try {
      await p2pStackService.publishMessage(sixId, topic, data);
      return { sixId, topic, success: true, timestamp: Date.now() };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ sixId, message: errorMessage });
    }
  },
);

export const subscribeToTopic = createAppAsyncThunk(
  'connection/subscribeTopic',
  (
    { sixId, topic, handler }: { sixId: string; topic: string; handler: (message: Uint8Array) => void },
    { extra: { pocServices }, rejectWithValue },
  ) => {
    const { p2pStackManagerService: p2pStackService, libp2pService } = pocServices;
    try {
      const libp2pNode = p2pStackService.getLibp2pNode(sixId);
      if (libp2pNode === null) {
        throw new Error(`LibP2P node not ready for six '${sixId}'`);
      }

      const unsubscribe: Unsubscribe = libp2pService.subscribeToTopic(libp2pNode, topic, handler);
      return { sixId, topic, unsubscribe };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ sixId, message: errorMessage });
    }
  },
);

/* -------------------------------------------------------------------------- */
/*  Slice                                                                     */
/* -------------------------------------------------------------------------- */

export const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    updateConnectedPeers: (state, { payload }: PayloadAction<{ sixId: string; count: number }>) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => ({
        ...connection,
        connectedPeers: payload.count,
      }));
    },

    updateConnectionDetails: (
      state,
      { payload }: PayloadAction<{
        sixId: string;
        connections: Array<{
          peerId: string;
          address: string;
          isDirect: boolean;
          isRelay: boolean;
          direction: 'inbound' | 'outbound';
        }>;
      }>
    ) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => {
        // Update connection details and peer connection status
        const updatedMessageDetails = connection.messageDetails.map(msg => ({
          ...msg,
          isConnected: payload.connections.some(conn => conn.peerId === msg.peerId)
        }));

        // Determine if we're "connected" - must have bootstrap/relay connection
        const hasRelayConnection = payload.connections.some(conn => conn.isRelay);

        // Update status based on bootstrap connection
        let newStatus: ConnectionStatus = connection.status;
        if (connection.isP2PStackInitialized) {
          newStatus = hasRelayConnection ? 'connected' : 'connecting';
        }

        return {
          ...connection,
          status: newStatus,
          connectionDetails: payload.connections,
          messageDetails: updatedMessageDetails,
          connectedPeers: payload.connections.length,
        };
      });
    },

    setInitializationProgress: (
      state,
      { payload }: PayloadAction<{
        sixId: string;
        step: keyof SingleConnectionState['initializationProgress'];
        completed: boolean;
      }>,
    ) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => ({
        ...connection,
        initializationProgress: {
          ...connection.initializationProgress,
          [payload.step]: payload.completed,
        },
      }));
    },

    setConnectionError: (state, { payload }: PayloadAction<{ sixId: string; error: string }>) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => ({
        ...connection,
        errorMessage: payload.error,
        status: 'error',
      }));
    },

    clearConnectionError: (state, { payload }: PayloadAction<string>) => {
      state.connections = updateConnection(state.connections, payload, connection => ({
        ...connection,
        errorMessage: null,
      }));
    },

    resetConnection: (state, { payload }: PayloadAction<string>) => {
      state.connections = updateConnection(
        state.connections,
        payload,
        connection => createInitialConnection(connection.sixId)
      );
    },

    resetAllConnections: () => initialState,

    removeSixConnection: (state, { payload }: PayloadAction<string>) => {
      state.connections = removeConnection(state.connections, payload);
    },

    unsubscribeFromTopic: (state, { payload }: PayloadAction<{ sixId: string; topic: string }>) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => {
        const { [payload.topic]: removedUnsubscribe, ...remainingTopics } = connection.activeTopics;
        return { ...connection, activeTopics: remainingTopics };
      });
    },

    recordSubscribedTopic: (
      state,
      { payload }: PayloadAction<{
        sixId: string;
        topic: string;
      }>
    ) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => {
        // Check if topic already exists
        const existingTopicIndex = connection.topicDetails.findIndex(t => t.topic === payload.topic);

        if (existingTopicIndex >= 0) {
          // Topic already tracked, don't duplicate
          return connection;
        }

        // Add new topic with zero message count
        const updatedTopicDetails = [...connection.topicDetails, {
          topic: payload.topic,
          messageCount: 0,
          lastMessage: 'No messages yet',
          lastSenderPeerId: '',
          lastMessageTime: 0,
          subscriberCount: 0
        }];

        return {
          ...connection,
          topicDetails: updatedTopicDetails
        };
      });
    },

    recordMessageStats: (
      state,
      { payload }: PayloadAction<{
        sixId: string;
        fromPeer: string;
        topic: string;
        timestamp: number;
        messageType: string;
        messageContent: string;
      }>
    ) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => {
        // Update peer message details
        const existingPeerIndex = connection.messageDetails.findIndex(m => m.peerId === payload.fromPeer);
        const updatedMessageDetails = [...connection.messageDetails];

        if (existingPeerIndex >= 0) {
          // Update existing peer stats
          const existing = updatedMessageDetails[existingPeerIndex];
          updatedMessageDetails[existingPeerIndex] = {
            ...existing,
            messageCount: existing.messageCount + 1,
            lastMessage: payload.messageContent,
            lastMessageTime: payload.timestamp,
            isConnected: existing.isConnected
          };
        } else {
          // Add new peer stats
          updatedMessageDetails.push({
            peerId: payload.fromPeer,
            messageCount: 1,
            lastMessage: payload.messageContent,
            lastMessageTime: payload.timestamp,
            isConnected: false // Will be updated by connection info
          });
        }

        // Update topic details
        const existingTopicIndex = connection.topicDetails.findIndex(t => t.topic === payload.topic);
        const updatedTopicDetails = [...connection.topicDetails];

        if (existingTopicIndex >= 0) {
          // Update existing topic stats
          const existing = updatedTopicDetails[existingTopicIndex];
          updatedTopicDetails[existingTopicIndex] = {
            ...existing,
            messageCount: existing.messageCount + 1,
            lastMessage: payload.messageContent,
            lastSenderPeerId: payload.fromPeer,
            lastMessageTime: payload.timestamp
          };
        } else {
          // Add new topic stats (shouldn't happen if recordSubscribedTopic was called)
          updatedTopicDetails.push({
            topic: payload.topic,
            messageCount: 1,
            lastMessage: payload.messageContent,
            lastSenderPeerId: payload.fromPeer,
            lastMessageTime: payload.timestamp,
            subscriberCount: 0
          });
        }

        return {
          ...connection,
          messageDetails: updatedMessageDetails,
          topicDetails: updatedTopicDetails
        };
      });
    },

    updateTopicSubscriberCounts: (
      state,
      { payload }: PayloadAction<{
        sixId: string;
        subscriberCounts: Record<string, number>;
      }>
    ) => {
      state.connections = updateConnection(state.connections, payload.sixId, connection => {
        const updatedTopicDetails = connection.topicDetails.map(topic => {
          const subscriberCount = payload.subscriberCounts[topic.topic] ?? 0;
          return {
            ...topic,
            subscriberCount
          };
        });

        return {
          ...connection,
          topicDetails: updatedTopicDetails
        };
      });
    },
  },

  extraReducers: (builder) => {
    builder
      // Initialize P2P Stack
      .addCase(initializeP2PStack.pending, (state, { meta }) => {
        const { sixId } = meta.arg;
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, connection => ({
          ...connection,
          status: 'connecting',
          errorMessage: null,
        }));
      })
      .addCase(initializeP2PStack.fulfilled, (state, { payload, meta }) => {
        state.connections = updateConnection(state.connections, payload.sixId, connection => {
          // Check if we already have relay connections (may have connected during async init)
          const hasRelayConnection = connection.connectionDetails.some(conn => conn.isRelay);
          const newStatus = hasRelayConnection ? 'connected' : 'connecting';

          return {
            ...connection,
            status: newStatus,
            peerId: payload.peerId,
            connectedPeers: payload.peers,
            isP2PStackInitialized: true,
          };
        });
      })
      .addCase(initializeP2PStack.rejected, (state, { meta, payload }) => {
        const { sixId } = meta.arg;
        const errorMessage = payload && typeof payload === 'object' && 'message' in payload
          ? String(payload.message)
          : 'Unknown error';

        state.connections = updateConnection(state.connections, sixId, connection => ({
          ...connection,
          status: 'error',
          errorMessage,
          isP2PStackInitialized: false,
        }));
      })

      // Disconnect P2P Stack
      .addCase(disconnectP2PStack.fulfilled, (state, { payload }) => {
        state.connections = updateConnection(state.connections, payload.sixId, connection => ({
          ...connection,
          status: 'disconnected',
          isP2PStackInitialized: false,
          peerId: null,
          connectedPeers: 0,
        }));
      })

      // Subscribe to Topic
      .addCase(subscribeToTopic.fulfilled, (state, { payload }) => {
        state.connections = updateConnection(state.connections, payload.sixId, connection => {
          const newActiveTopics = { ...connection.activeTopics };
          newActiveTopics[payload.topic] = payload.unsubscribe;
          return { ...connection, activeTopics: newActiveTopics };
        });
      })

      // Disconnect Peer
      .addCase(disconnectPeer.fulfilled, (state, { payload }) => {
        log.info(`Peer ${payload.peerId.substring(0, 20)}... disconnected from Six ${payload.sixId}`);
      })
      .addCase(disconnectPeer.rejected, (state, { payload }) => {
        if (payload && typeof payload === 'object' && 'sixId' in payload && 'message' in payload) {
          log.error(`Failed to disconnect peer in Six ${String(payload.sixId)}:`, payload.message);
        }
      })

      // Reconnect Peer
      .addCase(reconnectPeer.fulfilled, (state, { payload }) => {
        log.info(`Peer ${payload.peerId.substring(0, 20)}... reconnected to Six ${payload.sixId}`);
      })
      .addCase(reconnectPeer.rejected, (state, { payload }) => {
        if (payload && typeof payload === 'object' && 'sixId' in payload && 'message' in payload) {
          log.error(`Failed to reconnect peer in Six ${String(payload.sixId)}:`, payload.message);
        }
      })

  },
});

/* -------------------------------------------------------------------------- */
/*  Actions Export                                                            */
/* -------------------------------------------------------------------------- */

export const {
  updateConnectedPeers,
  updateConnectionDetails,
  setInitializationProgress,
  setConnectionError,
  clearConnectionError,
  resetConnection,
  resetAllConnections,
  removeSixConnection,
  unsubscribeFromTopic,
  recordSubscribedTopic,
  recordMessageStats,
  updateTopicSubscriberCounts,
} = connectionSlice.actions;

/* -------------------------------------------------------------------------- */
/*  Thunks Re-Export                                                          */
/* -------------------------------------------------------------------------- */

export { disconnectPeer } from '@/store/thunks/connection/disconnectPeer';
export { reconnectPeer } from '@/store/thunks/connection/reconnectPeer';

/* -------------------------------------------------------------------------- */
/*  Reducer Export                                                            */
/* -------------------------------------------------------------------------- */

export default connectionSlice.reducer;
